<template>
  <div class="w-screen min-h-screen bg-white absolute top-0 left-0 z-[2001] flex flex-col items-center justify-start">
    <!-- PAYMENT MODAL HEADER START -->
    <div
      v-if="!successMode"
      class="border-b border-cool-gray-20 !h-[86px] !shrink-0 w-full flex items-center justify-center"
    >
      <div
        class="max-w-5xl w-full flex justify-between"
      >
        <div
          class="flex cursor-pointer"
          @click="onModalClose"
        >
          <back-icon class="mr-2" />
          {{ $t('common.back') }}
        </div>
        <div
          class="cursor-pointer"
          @click="onModalClose"
        >
          <cross-icon class="text-cool-gray-50" />
        </div>
      </div>
    </div>
    <!-- PAYMENT MODAL HEADER END -->

    <!-- PAYMENT MODAL SUCCESS STATE START -->
    <div
      v-if="successMode"
      class="flex flex-1 w-full justify-center pt-8 overflow-y-auto"
    >
      <div
        v-if="successMode"
        class="flex flex-col items-center justify-center"
      >
        <payment-success-icon class="-mt-16" />
        <div class="mt-5 text-cool-gray-100 font-medium text-2xl">
          {{ $t('mycountly.dashboard.payment-modal.success-title') }}
        </div>
        <div class="mt-4 mb-8 text-cool-gray-100 text-xs max-w-[600px] text-center">
          {{ $t('mycountly.dashboard.payment-modal.success-description') }}
        </div>
        <el-button
          class="!p-3 w-[300px] !font-medium"
          type="success"
          @click="goToDashboard"
        >
          {{ $t('mycountly.dashboard.payment-modal.success-cta') }}
        </el-button>
      </div>
    </div>
    <!-- PAYMENT MODAL SUCCESS STATE END -->

    <!-- PAYMENT MODAL INITIAL STATE START -->
    <div
      v-else
      class="flex h-fit w-full justify-center py-8"
    >
      <div class="w-[400px] mr-[82px]">
        <div class="font-bold text-2xl mb-3">
          {{ $t('common.summary') }}
        </div>
        <div class="bg-cool-gray-10 p-8">
          <div class="flex justify-between items-center">
            <div class="flex flex-col">
              <div class="font-medium text-[13px]">
                {{ selectedTier.name || 'Name placeholder' }}
              </div>
              <div class="text-cool-gray-50 text-[13px]">
                {{ selectedTier.description }}
              </div>
            </div>
            <myc-price
              is-payment-modal
              :amount="selectedTier.pricing.price.amount.$numberDecimal"
            />
          </div>
          <div class="text-cool-gray-50 font-medium my-4">
            {{ $t('mycountly.dashboard.deployments.add-ons') }}
          </div>
          <div
            v-for="(feature, index) in paymentParams.provisionSummary.items.filter((i) => !i.meta.basePrice && !i.meta.size)"
            :key="index"
            class="flex justify-between mb-4"
          >
            <div class="flex flex-col">
              <div class="font-medium text-[13px]">
                {{ feature.name || 'Name placeholder' }}
              </div>
              <div class="text-cool-gray-50 text-[13px]">
                {{ feature.description }}
              </div>
            </div>

            <myc-price
              is-payment-modal
              :amount="feature.pricing.price.amount"
            />
          </div>
          <div class="border-t border-cool-gray-20 pt-4">
            <div class="flex justify-between mb-4">
              <div class="font-medium text-[13px]">
                {{ $t('common.tax') }}
              </div>
              <div class="text-xs text-cool-gray-50 w-3/4 text-right">
                {{ $t('mycountly.dashboard.payment-modal.tax-description') }}
              </div>
            </div>
            <div class="flex justify-between">
              <div class="font-medium text-[13px]">
                {{ $t('common.total') }}
              </div>
              <div class="flex flex-col items-end justify-center">
                <myc-price
                  is-payment-modal
                  :amount="paymentParams.provisionSummary.total.amount"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="checkout-container" />
    </div>
    <!-- PAYMENT MODAL INITIAL STATE START -->
  </div>
</template>

<script>
/* global Paddle */
import { mapActions, mapState } from 'pinia';
import { useDeploymentStore } from '@/stores';
import { countlyVariables } from '@/constants';
import CrossIcon from '@/components/icons/cross.vue';
import BackIcon from '@/components/icons/back.vue';
import PaymentSuccessIcon from '@/components/icons/payment-success.vue';
import MycPrice from '@/components/common/myc-price.vue';

const isProd = window.location.hostname === 'flex.countly.com';

export default {
  components: {
    PaymentSuccessIcon,
    CrossIcon,
    BackIcon,
    MycPrice,
  },
  computed: {
    ...mapState(useDeploymentStore, ['paymentParams', 'plans', 'activePaymentPeriod', 'successMode']),
    selectedTier() {
      return this.paymentParams.provisionSummary.items.find((i) => i.description === 'Base price');
    },
    paddleCustomData() {
      return (this.paymentParams.provisionSummary && this.paymentParams.provisionSummary.dataToVendor) ? this.paymentParams.provisionSummary.dataToVendor : {};
    },
  },
  mounted() {
    if (!this.successMode) {
      Paddle.Setup({
        seller: isProd ? countlyVariables.PADDLE_SELLER_PROD : countlyVariables.PADDLE_SELLER_DEV,
        eventCallback: (data, error) => {
          if (data.name === 'checkout.completed') {
            this.$router.push({
              name: 'dashboardRoot',
              query: {
                success: true,
              },
            });
          }
        },
      });

      Paddle.Checkout.open({
        settings: {
          displayMode: 'inline',
          frameTarget: 'checkout-container', // className of your checkout <div>
          frameInitialHeight: 450, // `450` or above
          frameStyle: 'width:100%; min-width:312px; background-color: transparent; border: none;', // `min-width` must be set to `286px` or above with checkout padding off; `312px` with checkout padding on.
        },
        customData: this.paddleCustomData,
        items: this.paymentParams.items,
        customer: {
          email: this.paymentParams.customer.email,
        },
      });
    }
  },
  methods: {
    ...mapActions(useDeploymentStore, ['setShowPaymentModal', 'setSuccessMode']),
    goToDashboard() {
      this.setShowPaymentModal(false);
      this.setSuccessMode(false);
    },
    onModalClose() {
      this.setShowPaymentModal(false);
    },
    onBack() {
      this.setShowPaymentModal(false);
    },
  },
};
</script>
